import * as ko from 'knockout';

import BaseInputBindable from './baseInputBindable';
import SelectInputBindableArgs from './selectInputBindableArgs';
import SelectOption from '../webHostClient/selectOption';

//
// Bindable for select controls
//
// NOTE: You need to use the if binding to hide the control when loading (not the visible binding)
// to prevent the options observable update messing with the selected observable. This is because the
// if binding works by removing the select control from the DOM and the visible binding works by setting
// 'display:none;'.

export class SelectInputBindable extends BaseInputBindable {
    public readonly selected: ko.Observable<number> = ko.observable(null);
    public readonly options: ko.ObservableArray<SelectOption> = ko.observableArray([]);
    public readonly placeholderText: ko.Observable<string> = ko.observable<string>("");
    public readonly includeDummyOption: ko.Computed<boolean>;

    public readonly optionsCaption: ko.Computed<string>;

    private _selectedSubscription: ko.Subscription;

    constructor(args: SelectInputBindableArgs) {
        super(args);
        if (args.placeholderText) {
            this.placeholderText(args.placeholderText);
        }

        // If we have placeholder text or if we are allowing no option to be selected show the dummy item.
        // Also if we have no item selected, don't preselect the top item.
        // NOTE: The if binding must be used to remove the select control from the DOM while isLoading is true for this to work.
        this.includeDummyOption = ko.computed(() =>
            !this.selected() ||
            !this.isRequired() ||
            !!this.placeholderText()
        );

        // When the value changes show the validation state to the user
        this._selectedSubscription = this.selected.subscribe(this._selectedChanged, this);

        this.optionsCaption = ko.computed(() => {
            let caption: string;
            if (this.includeDummyOption()) {
                caption = this.placeholderText();
            }
            else {
                caption = null;
            }
            return caption;
        });
    }

    //
    // Set the options, the selected value and hide any validation error
    public loadComplete(selected: number, options: SelectOption[], canChange?: boolean): void {
        this._selectedSubscription.dispose();

        this.options.removeAll();
        options.forEach(o => this.options.push(o));
        this.selected(selected);

        this.updateCanChange(canChange);

        this.showValidationState(false);
        this.isLoading(false);

        this._selectedSubscription = this.selected.subscribe(this._selectedChanged, this);
    }

    protected hasValidationError(): boolean {
        if (super.hasValidationError()) {
            return true;
        }

        let validationError: boolean;
        if (this.isRequired()) {
            validationError = !(
                (null !== this.selected()) && this.options().some((option: SelectOption) => option.value === this.selected())
            );
        }
        else {
            validationError = false;
        }

        return validationError;
    }

    private _selectedChanged(newValue: number): void {
        if (this.hasFocus()) {
            this.showValidationState(true);
        }
    }
}